import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeContext } from 'styled-components';
import classNames from 'classnames';
import postal from 'postal';
import { useSpring, animated } from 'react-spring';
import AnimatedProfileFollowButton from '../../shared/animated-profile-follow-button';
import IconedButton from '../../shared/iconed-button';
import EnquiryContext from '../../shared/enquiry/context';
import { recordStatisticsEvent } from '../../../services/statistics-service';

const height = 68;
const Container = styled(animated.div)`
  z-index: 100;
  width: 100vw;
  height: ${height}px;
  background: white;
  position: fixed;
  bottom: 0px;
  box-shadow: 0px -4px 15px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: flex-end;
  padding: 10px 20px 22px 20px;

  & > button {
    margin: 0;
    margin-left: 8px;
    height: 36px;
    align-items: center;
    outline: none;
  }

  & > button[name="profile-follow-button"] {
    width: 107px;
  }

  &:not(.discreet) > button:not([name="profile-follow-button"]) {
      width: 33px;
      padding: 0;
      background-color: ${props => props.theme.bodyColor};

      > span:not(.icon-lw) {
        display: none;
      }
  }


  &&.discreet {
    > button[name="profile-follow-button"] {
      display: none;
    }

    > button:not([name="profile-follow-button"]) {
      background-color: #3c4966;
      min-width: 107px;
      width: 107px;
      > span.icon-lw-contact {
        padding: 3px;
      }
    }
  }

  &&&& > button:not([name="profile-follow-button"]) {
    color: white;

    > span.icon-lw-contact {
      background-color: ${props => props.theme.bodyColor};
    }
  }
`;

const Desc = styled.div`
  flex-grow: 2;
  flex-basis: 50%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  overflow-x: hidden;
`;

const Name = styled.span`
  font: ${props => props.theme.fontWeightHeavy} 16px/18px ${props => props.theme.fontFamilyBody};
  color: ${props => props.theme.bodyColor};
  overflow-x: hidden;
  width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Org = styled.span`
  font: ${props => props.theme.fontWeightMedium} 14px/120% ${props => props.theme.fontFamilyBody};
  color: #4b6072;
  overflow-x: hidden;
  width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const followTopic = {
  channel: 'micro-profile.commands',
  topic: 'follow-status',
};

export default function StickyFooter({
  profile, followed, yOffset,
}) {
  const { isMobile } = React.useContext(ThemeContext);
  const { dispatch } = React.useContext(EnquiryContext);
  const [discreet, setDiscreet] = React.useState(followed);
  const showContact = _.includes(profile.activeFeatures, 'premium_enquiry_form')
    && _.get(profile, 'activePartnership');

  const isVisible = React.useCallback(
    () => ((!discreet || showContact) && (yOffset > (isMobile ? 255 : 265))),
    [yOffset, discreet, showContact, isMobile],
  );

  const styles = useSpring({
    opacity: isVisible() ? 1 : 0,
    transform: `translateY(${isVisible() ? 0 : height}px)`,
  });

  const followProps = {
    isProfileFollowedByCurrentUser: false,
    profileId: profile.id,
    followEventName: 'mobile_footer_follow_profile',
  };

  const contact = e => {
    e && e.preventDefault();

    recordStatisticsEvent({
      event: 'mobile_footer_contact_profile',
      statisticable: 'profile',
      id: profile.id,
      page: window.location.pathname,
    });

    if (_.get(window, 'Livewire.currentUser.id')) {
      dispatch({ type: 'showEnquiryForm', payload: { scroll: true, offset: -200 } });
    } else {
      window.location = '/sign_up?return_to_section=enquiry';
    }
  };

  React.useEffect(() => () => postal.unsubscribe(followTopic), []);

  React.useEffect(() => {
    postal.subscribe({
      ...followTopic,
      callback: d => d.id === profile.id && setDiscreet(d.following && followed),
    });
  }, [profile.id, followed]);

  return (
    <Container style={styles} className={classNames({ discreet })}>
      <Desc>
        <Name>{profile.name}</Name>
        <Org>{profile.organization}</Org>
      </Desc>
      <AnimatedProfileFollowButton {...followProps} />
      {!!showContact && (
        <IconedButton type="button" className="btn" onClick={contact}>
          <span className="icon-lw icon-lw-contact" />
          <span>contact</span>
        </IconedButton>
      )}
    </Container>
  );
}

StickyFooter.propTypes = {
  profile: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    organization: PropTypes.string,
    activeFeatures: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  followed: PropTypes.bool.isRequired,
  yOffset: PropTypes.number.isRequired,
};
